import highlightUtil from '@/lib/highlightUtil'
import * as definition from '@/lib/highlightMappings'

/**
 * ハイライト作成に関わる処理をまとめたmixin
 * JobPrePublishedPreview以外からの利用想定なし
 */
export default {
  methods: {
    async selectedAction (e = null) {
      // エリアハイライトとテキストハイライト切り分け
      const targetId = e?.composedPath()?.find(p => p.id)?.id

      // エリアハイライトか否かの判定(1)画像系
      if (highlightUtil.isAreaHighlight(targetId)) {
        await this.createAreaHighlight(document.getElementById(targetId))
        return
      }

      // 選択位置の取得
      const selection = window.getSelection()

      if (!selection?.anchorNode?.nodeName || selection.anchorNode.nodeName !== '#text') {
        // textノード以外を選択されている場合は処理なし
        return
      }

      const selectItem = {
        text: selection.toString(),
        target: highlightUtil.getId(selection.focusNode) // 対象エリアを親IDから取得
      }

      // エリアハイライトか否かの判定(2)文字もあるタイプ
      if (highlightUtil.isAreaHighlight(selectItem.target)) {
        await this.createAreaHighlight(document.getElementById(selectItem.target))
        return
      }

      // 親IDでエリアが特定できない場合には処理なしで終了させる
      if (selectItem.target === highlightUtil.topParentHighlightId) return

      // ハイライト済みの箇所を選択された場合は削除処理を実施
      if (highlightUtil.isHighlighted(selection)) {
        if (!selectItem.text.length) { // クリックされた場合
          this.deleteHighlightElement(selection, selectItem)
          return
        } else {
          this.$toasted.global.error({ message: '既にハイライトされているため、ハイライトできませんでした。' })
          // 範囲選択の削除
          window.getSelection().empty()
          return
        }
      }

      // ハイライト箇所以外の0文字（＝クリックされた場合）なら処理なし
      if (!selectItem.text.length) return

      // ハイライト情報の保管
      await this.addHighlight(selection, selectItem)
    },

    // ハイライト削除
    deleteHighlightElement (selection, selectItem) {
      // コメントを取得
      const parentNode = selection.anchorNode.parentNode
      const commentId = selectItem.target.replace(`${highlightUtil.highlightIdPrefix}-`, '')
      const thread = this.commentHighlights.find(i => i.highlight && `${i.target}-${i.highlight.start}` === commentId)

      this.checkCommentExistsAndDeleteThread(thread).then(isDelete => {
        if (isDelete) {
          highlightUtil.deleteTextHighlight(parentNode)
        }
      })
    },

    // スレッド削除可否
    canDeleteThread (thread) {
      if (this.isCompanyStaffAccess && !thread.isCompanyStaff) {
        // 企業担当者は、企業担当者以外が作成したハイライトを削除できない
        this.$toasted.global.error({ message: '相手側の作成したハイライトであるため、削除できません。' })
        return false
      }
      return true
    },

    // コメントがなければ削除確認をしない
    async checkCommentExistsAndDeleteThread (thread) {
      // スレッド削除可否チェック
      if (!this.canDeleteThread(thread)) return false

      if (thread.comments.length || thread.addingComment?.comment.trim().length > 0) {
        // 削除確認あり
        return await this.deleteThread(thread)
      } else {
        // 削除確認無し
        return this.deleteThreadAction(thread)
      }
    },

    // ハイライト付与
    async addHighlight (selection, selectItem) {
      if (highlightUtil.getId(selection.anchorNode) !== highlightUtil.getId(selection.focusNode)) {
        // Rangeが複数のエリアを跨いでいる場合は処理を実施せず返却
        this.$toasted.global.error({ message: 'エリアを跨いでいる、または項目名を含んでいるため、ハイライトできませんでした。' })
        return
      }
      // ハイライトの付与（DOM書き換え）
      const newHighlight = document.createElement('mark')
      newHighlight.className = `pega-highlight ${highlightUtil.getHighlightClass(this.isCompanyStaffAccess)}`
      const selectionRange = selection.getRangeAt(0)
      newHighlight.appendChild(selectionRange.extractContents())
      selectionRange.insertNode(newHighlight)

      // 開始位置と終了位置の取得
      const afterRange = document.createRange()
      let parentNode = selection.focusNode
      while (!parentNode.id) { parentNode = parentNode.parentNode }
      afterRange.setStart(parentNode, 0)
      afterRange.setEnd(selection.anchorNode, selection.anchorOffset)
      const startIndex = afterRange.toString().length
      selectItem.index = {
        start: startIndex,
        end: startIndex + selectItem.text.length
      }

      await this.createHighlight(selectItem, newHighlight)
    },

    // ハイライト作成（共通部）
    async createHighlight (selectItem, highlightElement) {
      // ハイライト必要データ準備
      const request = {
        target: selectItem.target,
        targetText: highlightUtil.truncateHighlightText(selectItem.text),
        isOpen: true,
        isCompanyStaff: this.isCompanyStaffAccess,
        highlight: {
          start: selectItem.index.start,
          end: selectItem.index.end
        }
      }
      const rectData = highlightUtil.setOffsetPosition({}, highlightElement)
      // ハイライト登録API
      await this.$http.secured.post(this.apiPath + '/threads', request).then(response => {
        // 結果データを追加
        const commentHighlight = response.data
        this.addComment(commentHighlight)
        this.commentHighlights.push({ ...commentHighlight, ...rectData })
      })

      // ハイライトエレメントにIDを指定
      highlightElement.id = highlightUtil.createHighlightId(selectItem.target, selectItem.index.start)

      this.sortHighlightData()

      this.$toasted.global.success({ message: 'ハイライトを作成しました。' })
    },

    // 新規空コメント生成
    addComment (thread) {
      const addComment = {
        id: null,
        comment: '',
        threadId: thread.id,
        threadTitle: thread.targetText
      }
      this.$set(thread, 'addingComment', addComment)
      this.pendingComments.push(thread.addingComment)
    },

    // 画像ハイライト作成
    async createAreaHighlight (element) {
      const thread = this.getCommentHighlightOnlyOne(element.id)
      if (thread) {
        this.checkCommentExistsAndDeleteThread(thread).then(isDelete => {
          if (isDelete) {
            highlightUtil.deleteAreaHighlight(element)
          }
        })
        return
      }
      const newHighlight = highlightUtil.areaHighlight(element, highlightUtil.getHighlightClass(this.isCompanyStaffAccess))
      if (!newHighlight) return
      const selectItem = {
        target: element.id,
        text: definition.areaLabelMap[element.id],
        index: {
          start: 0,
          end: 1
        }
      }
      await this.createHighlight(selectItem, newHighlight)
    }
  }
}
